import { useEffect, useMemo, useState } from "preact/hooks";
import type { Entry, IndexRecord } from "../../shared/snapshot-format";
import type { Character } from "../character";
import { loadCatalogue, searchCatalogue } from "../data/catalogue";
import { loadEntry } from "../data/entries";
import type { DerivedSheet } from "../engine";
import type { PlayAction, RollLog, SessionMaxima } from "../session";
import { badgeOfEntry, conditionValue, describeDuration, describePer, describeRemaining, durationOfEntry, isLocked, newestUndoable } from "../session";
import { RollButton } from "./roll-button";
import { useSheetActions } from "./sheet-context";

interface Props {
  character: Character;
  sheet: DerivedSheet;
  maxima: SessionMaxima | null;
  busy: boolean;
  /** The Roll Log (#20): its newest entry's events show here, and Undo reverses it. */
  log: RollLog;
  onPlay: (action: PlayAction, label: string) => Promise<void>;
  onUndo: (id: string) => Promise<void>;
  /** Open the Roll Log tab. */
  onLog: () => void;
  onStart: () => Promise<void>;
  onClose: () => Promise<void>;
}

const EFFECT_PACKS = new Set(["feat-effects", "spell-effects", "equipment-effects", "other-effects", "boons-and-curses", "campaign-effects"]);
const DAMAGE_TYPES = ["acid", "bleed", "bludgeoning", "cold", "electricity", "fire", "force", "mental", "piercing", "poison", "slashing", "sonic", "spirit", "vitality", "void"];

/**
 * The Session tab (issue #18): HP and the death track, Conditions and Effects with add and
 * remove, the clock, Hero Points, focus, resources, uses and consumable stacks, daily
 * preparations and start fresh. Every control emits a Play Action; the page applies it and the
 * Roll Log (#20) records it; the newest entry's events show here with Undo, the rest is the Roll Log tab.
 */
export function SessionTab({ character, sheet, maxima, busy, log, onPlay, onUndo, onLog, onStart, onClose }: Props) {
  const session = character.session;
  if (!session || !maxima) {
    return (
      <section class="session">
        <h3>Session</h3>
        <p class="muted">No Session is open. Starting one takes the Character into play at its derived maxima: full HP, one Hero Point, every slot and use fresh.</p>
        <button type="button" class="primary" disabled={busy} onClick={() => void onStart()}>Start Session</button>
      </section>
    );
  }
  const play = (action: PlayAction, label: string) => void onPlay(action, label);
  const dying = conditionValue(session, "dying");
  const wounded = conditionValue(session, "wounded");
  const doomed = conditionValue(session, "doomed");
  const dead = dying > 0 && dying >= sheet.death.dying.max;
  const inMemory = sheet.items.filter((i) => i.inMemory && (i.type === "condition" || i.type === "effect"));
  const last = log.entries[log.entries.length - 1];
  const undoable = newestUndoable(log);

  return (
    <section class="session">
      <div class="row">
        <h3>Session</h3>
        <span class="actions">
          <span class="muted">{session.round !== null ? `Round ${session.round}` : "No encounter"} · {describeClock(session.clock)}</span>
          {undoable && <button type="button" class="undo" disabled={busy} onClick={() => void onUndo(undoable.id)} title={undoable.summary}>Undo {undoable.summary}</button>}
          <button type="button" class="linkish" onClick={onLog}>Roll Log ({log.entries.length})</button>
        </span>
      </div>
      {dead && <p class="notice">Dying {dying} of {sheet.death.dying.max}: the Character is dead.</p>}
      {last && last.events.length > 0 && (
        <ul class="plain events">
          {last.events.map((e, i) => <li key={i} class={e.kind === "blocked" ? "notice" : e.kind === "dead" || e.kind === "massive-damage" ? "warn" : ""}><span class="tag">{e.kind === "roll-needed" ? "roll" : e.kind}</span> {e.text}</li>)}
        </ul>
      )}

      <HitPoints session={session} maxima={maxima} busy={busy} play={play} />

      <div class="vitals">
        <Track label="Dying" value={dying} max={sheet.death.dying.max} busy={busy}
          onUp={() => play({ kind: "apply-condition", slug: "dying" }, "dying +1")}
          onDown={() => { const c = session.conditions.find((c) => c.slug === "dying"); if (c) play({ kind: "reduce-condition", id: c.id }, "dying -1"); }} />
        <Track label="Wounded" value={wounded} max={sheet.death.wounded.max} busy={busy}
          onUp={() => play({ kind: "apply-condition", slug: "wounded" }, "wounded +1")}
          onDown={() => { const c = session.conditions.find((c) => c.slug === "wounded"); if (c) play({ kind: "reduce-condition", id: c.id }, "wounded -1"); }} />
        <Track label="Doomed" value={doomed} max={sheet.death.doomed.max} busy={busy}
          onUp={() => play({ kind: "apply-condition", slug: "doomed" }, "doomed +1")}
          onDown={() => { const c = session.conditions.find((c) => c.slug === "doomed"); if (c) play({ kind: "reduce-condition", id: c.id }, "doomed -1"); }} />
        <div class="track">
          <span class="stat-label">Hero Points</span>
          <span class="pips">
            <button type="button" disabled={busy || session.heroPoints <= 0} onClick={() => play({ kind: "spend-hero-point" }, "Hero Point spent")}>−</button>
            <span class="stat-value">{session.heroPoints}</span>
            <button type="button" disabled={busy || session.heroPoints >= maxima.heroPoints.max} onClick={() => play({ kind: "gain-hero-point" }, "Hero Point gained")}>+</button>
          </span>
        </div>
        {maxima.focus.max > 0 && (
          <div class="track">
            <span class="stat-label">Focus</span>
            <span class="pips">
              <button type="button" disabled={busy || session.focus <= 0} onClick={() => play({ kind: "spend-focus" }, "Focus Point spent")}>−</button>
              <span class="stat-value">{session.focus} / {maxima.focus.max}</span>
              <button type="button" disabled={busy || session.focus >= maxima.focus.max} onClick={() => play({ kind: "refocus" }, "Refocus")}>+</button>
            </span>
          </div>
        )}
      </div>
      {dying > 0 && (
        <p class="inline">
          <RollButton class="roll-check" request={{ kind: "recovery" }} disabled={busy}>
            <span class="roll-label">Recovery check</span>
            <span class="roll-mod">DC {sheet.death.dying.recoveryDC + dying}</span>
          </RollButton>
          <span class="muted">or enter the outcome:</span>
          {(["criticalSuccess", "success", "failure", "criticalFailure"] as const).map((degree) => (
            <button type="button" key={degree} disabled={busy} onClick={() => play({ kind: "dying-recovery", degree }, `recovery ${DEGREE_LABELS[degree]}`)}>{DEGREE_LABELS[degree]}</button>
          ))}
        </p>
      )}
      {maxima.fastHealing.filter((f) => f.enabled && typeof f.value === "string").map((f, i) => (
        <p key={i} class="inline">
          <RollButton class="roll-damage" request={{ kind: "fast-healing", formula: f.value as string, label: `Fast healing (${f.sourceName ?? "an Entry"})` }} disabled={busy}>
            <span class="roll-label">Fast healing</span>
            <span class="roll-mod">{String(f.value)}</span>
          </RollButton>
          <span class="muted">each turn, from {f.sourceName ?? "an Entry"}</span>
        </p>
      ))}

      <Conditions session={session} sheet={sheet} maxima={maxima} busy={busy} play={play} inMemory={inMemory.filter((i) => i.type === "condition")} />
      <Effects session={session} sheet={sheet} busy={busy} play={play} />

      <details open>
        <summary>Time</summary>
        <p class="inline">
          {session.round === null
            ? <button type="button" disabled={busy} onClick={() => play({ kind: "start-encounter" }, "encounter started")}>Start encounter</button>
            : <button type="button" disabled={busy} onClick={() => play({ kind: "end-encounter" }, "encounter ended")}>End encounter</button>}
          <button type="button" disabled={busy} onClick={() => play({ kind: "advance", seconds: 6 }, "1 round")}>Advance 1 round</button>
          <button type="button" disabled={busy} onClick={() => play({ kind: "advance", seconds: 600 }, "10 minutes")}>Advance 10 minutes</button>
          <button type="button" disabled={busy} onClick={() => play({ kind: "advance", seconds: 3600 }, "1 hour")}>Advance 1 hour</button>
        </p>
      </details>

      <Resources session={session} maxima={maxima} busy={busy} play={play} />
      <Consumables session={session} busy={busy} play={play} />

      {maxima.spellcasting.length === 0 ? (
        <p class="muted">Spell slots and innate uses appear here once the Character has Spellcasting Entries (#10).</p>
      ) : (
        <Spellcasting session={session} maxima={maxima} sheet={sheet} busy={busy} play={play} />
      )}

      <p class="inline session-end">
        <ConfirmButton label="Daily preparations" confirm="Rest for the night?" disabled={busy} onConfirm={() => play({ kind: "daily-preparations", at: new Date().toISOString() }, "daily preparations")} />
        <ConfirmButton label="Start fresh" confirm="Reset everything and clear the Roll Log?" disabled={busy} onConfirm={() => play({ kind: "start-fresh", at: new Date().toISOString() }, "start fresh")} />
        <ConfirmButton label="Close Session" confirm="Discard the play state?" disabled={busy} danger onConfirm={() => void onClose()} />
      </p>
    </section>
  );
}

const DEGREE_LABELS = { criticalSuccess: "Critical success", success: "Success", failure: "Failure", criticalFailure: "Critical failure" } as const;

function describeClock(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}h ${m}m ${s}s in`;
}

type Play = (action: PlayAction, label: string) => void;

function HitPoints({ session, maxima, busy, play }: { session: NonNullable<Character["session"]>; maxima: SessionMaxima; busy: boolean; play: Play }) {
  const [amount, setAmount] = useState("");
  const [critical, setCritical] = useState(false);
  const [type, setType] = useState("untyped");
  const n = Math.max(0, Math.trunc(Number(amount)) || 0);
  const hasIWR = maxima.iwr.immunities.length > 0 || maxima.iwr.weaknesses.length > 0 || maxima.iwr.resistances.length > 0;
  return (
    <div class="hp card">
      <div class="row">
        <span><span class="stat-label">HP</span> <span class="stat-value">{session.hp.value} / {maxima.hp.max}</span>{session.hp.temp > 0 && <span class="muted"> +{session.hp.temp} temporary</span>}</span>
        <span class="inline">
          <input type="number" min="0" inputMode="numeric" placeholder="amount" value={amount} onInput={(e) => setAmount((e.target as HTMLInputElement).value)} style="width:6rem" />
          <select aria-label="Damage type" value={type} title={hasIWR ? "The type the immunities, weaknesses and resistances apply to" : "Damage type"} onChange={(e) => setType((e.target as HTMLSelectElement).value)}>
            <option value="untyped">untyped</option>
            {DAMAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label class="toggle"><input type="checkbox" checked={critical} onChange={(e) => setCritical((e.target as HTMLInputElement).checked)} /> crit</label>
          <button type="button" class="danger" disabled={busy || n === 0} onClick={() => { play({ kind: "damage", amount: n, critical, ...(type !== "untyped" ? { type } : {}) }, `${n}${type !== "untyped" ? ` ${type}` : ""} damage`); setAmount(""); setCritical(false); }}>Damage</button>
          <button type="button" disabled={busy || n === 0} onClick={() => { play({ kind: "heal", amount: n }, `${n} healing`); setAmount(""); }}>Heal</button>
          <button type="button" disabled={busy || amount === ""} onClick={() => { play({ kind: "set-temp-hp", value: n }, `temporary HP ${n}`); setAmount(""); }}>Set temp HP</button>
        </span>
      </div>
    </div>
  );
}

function Track({ label, value, max, busy, onUp, onDown }: { label: string; value: number; max: number; busy: boolean; onUp: () => void; onDown: () => void }) {
  return (
    <div class={`track${value > 0 ? " on" : ""}`}>
      <span class="stat-label">{label}</span>
      <span class="pips">
        <button type="button" disabled={busy || value <= 0} onClick={onDown}>−</button>
        <span class="stat-value">{value} / {max}</span>
        <button type="button" disabled={busy || value >= max} onClick={onUp}>+</button>
      </span>
    </div>
  );
}

function Conditions({ session, sheet, maxima, busy, play, inMemory }: { session: NonNullable<Character["session"]>; sheet: DerivedSheet; maxima: SessionMaxima; busy: boolean; play: Play; inMemory: DerivedSheet["items"] }) {
  const { openEntry } = useSheetActions();
  const slugs = useMemo(() => Object.keys(maxima.conditions).sort(), [maxima]);
  const [slug, setSlug] = useState("frightened");
  const [value, setValue] = useState("1");
  const [formula, setFormula] = useState("1d6");
  const [damageType, setDamageType] = useState("fire");
  const known = maxima.conditions[slug];
  const nameOf = (id: string) => session.conditions.find((c) => c.id === id)?.name ?? session.effects.find((e) => e.id === id)?.name ?? id;
  const apply = () => {
    if (slug === "persistent-damage") play({ kind: "apply-condition", slug, persistent: { formula: formula.trim() || "1d6", damageType, dc: 15 } }, `persistent ${damageType}`);
    else if (known?.valued) play({ kind: "apply-condition", slug, value: Math.max(1, Math.trunc(Number(value)) || 1) }, `${known.name} applied`);
    else play({ kind: "apply-condition", slug }, `${known?.name ?? slug} applied`);
  };
  return (
    <details open>
      <summary>Conditions ({session.conditions.length}{inMemory.length > 0 ? ` + ${inMemory.length} in memory` : ""})</summary>
      <ul class="plain items">
        {session.conditions.map((c) => {
          const item = sheet.items.find((i) => i.sessionId === c.id);
          const locked = isLocked(session, c);
          return (
            <li key={c.id} class={item && !item.active ? "muted" : ""}>
              <span>
                <button type="button" class="linkish strong" onClick={() => openEntry(c.uuid, { badge: c.value ?? undefined })}>{c.name}</button>{c.value !== null ? ` ${c.value}` : ""}{c.persistent ? ` ${c.persistent.formula} ${c.persistent.damageType} (DC ${c.persistent.dc})` : ""}
                {c.grant ? <span class="muted"> · applied by {nameOf(c.grant.grantedBy)}</span> : null}
                {item && !item.active ? <span class="muted"> · overridden by {item.overriddenBy.join(", ")}</span> : null}
                {!item ? <span class="tag">Entry not loaded</span> : null}
              </span>
              <span class="actions">
                {c.persistent && (
                  <RollButton class="roll-damage" request={{ kind: "persistent-damage", conditionId: c.id }} disabled={busy} title="Roll the persistent damage; then the flat check to end it">
                    <span class="roll-label">Roll</span>
                    <span class="roll-mod">{c.persistent.formula}</span>
                  </RollButton>
                )}
                {c.value !== null && <button type="button" disabled={busy || locked} onClick={() => play({ kind: "reduce-condition", id: c.id }, `${c.name} -1`)}>−</button>}
                {c.value !== null && <button type="button" disabled={busy || locked} onClick={() => play({ kind: "apply-condition", slug: c.slug }, `${c.name} +1`)}>+</button>}
                <button type="button" disabled={busy || locked} title={locked ? "Held in place by what applied it" : "Remove"} onClick={() => play({ kind: "clear-condition", id: c.id }, `${c.name} removed`)}>×</button>
              </span>
            </li>
          );
        })}
        {inMemory.map((i) => (
          <li key={`${i.uuid}:${i.grantedBy?.uuid}`} class={i.active ? "" : "muted"}>
            <span><button type="button" class="linkish strong" onClick={() => openEntry(i.uuid, { badge: i.badge ?? undefined })}>{i.name}</button>{i.badge !== null ? ` ${i.badge}` : ""} <span class="tag">in memory</span>{i.grantedBy ? <span class="muted"> · applied by {i.grantedBy.name}</span> : null}{i.active ? null : <span class="muted"> · overridden by {i.overriddenBy.join(", ")}</span>}</span>
          </li>
        ))}
      </ul>
      <p class="inline">
        <select value={slug} onChange={(e) => setSlug((e.target as HTMLSelectElement).value)}>
          {slugs.map((s) => <option key={s} value={s}>{maxima.conditions[s]!.name}</option>)}
        </select>
        {known?.valued && <input type="number" min="1" inputMode="numeric" value={value} onInput={(e) => setValue((e.target as HTMLInputElement).value)} style="width:4rem" />}
        {slug === "persistent-damage" && (
          <>
            <input type="text" value={formula} placeholder="formula" onInput={(e) => setFormula((e.target as HTMLInputElement).value)} style="width:5rem" />
            <select value={damageType} onChange={(e) => setDamageType((e.target as HTMLSelectElement).value)}>{DAMAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          </>
        )}
        <button type="button" disabled={busy || !known} onClick={apply}>Apply</button>
      </p>
    </details>
  );
}

function Effects({ session, sheet, busy, play }: { session: NonNullable<Character["session"]>; sheet: DerivedSheet; busy: boolean; play: Play }) {
  const { openEntry } = useSheetActions();
  const [records, setRecords] = useState<IndexRecord[]>();
  const [text, setText] = useState("");
  const [level, setLevel] = useState("");
  useEffect(() => {
    loadCatalogue().then((all) => setRecords(all.filter((r) => r.t === "effect" && EFFECT_PACKS.has(r.p))), () => setRecords([]));
  }, []);
  const results = records && text ? searchCatalogue(records, { text, legacy: true, limit: 8 }) : [];
  const nameOf = (id: string) => session.conditions.find((c) => c.id === id)?.name ?? session.effects.find((e) => e.id === id)?.name ?? id;
  const add = async (record: IndexRecord) => {
    const entry: Entry | undefined = await loadEntry(record.u);
    if (!entry) return;
    const lvl = level.trim() === "" ? null : Math.max(0, Math.trunc(Number(level)) || 0);
    play({ kind: "apply-effect", uuid: entry.uuid, slug: entry.slug, name: entry.name, duration: durationOfEntry(entry), badge: badgeOfEntry(entry), level: lvl }, `${entry.name} applied`);
    setText("");
  };
  return (
    <details open>
      <summary>Effects ({session.effects.length})</summary>
      <ul class="plain items">
        {session.effects.map((e) => {
          const item = sheet.items.find((i) => i.sessionId === e.id);
          const locked = isLocked(session, e);
          return (
            <li key={e.id}>
              <span>
                <button type="button" class="linkish strong" onClick={() => openEntry(e.uuid, { badge: e.badge ?? undefined })}>{e.name}</button>{e.badge !== null ? ` ${e.badge}` : ""}{e.level !== null ? <span class="muted"> · level {e.level}</span> : null}
                <span class="muted"> · {describeRemaining(e, session.clock)} ({describeDuration(e.duration)})</span>
                {e.grant ? <span class="muted"> · applied by {nameOf(e.grant.grantedBy)}</span> : null}
                {!item ? <span class="tag">not applied</span> : null}
              </span>
              <span class="actions">
                {e.badge !== null && <button type="button" disabled={busy || e.badge <= 1} onClick={() => play({ kind: "set-badge", id: e.id, value: (e.badge ?? 1) - 1 }, `${e.name} -1`)}>−</button>}
                {e.badge !== null && <button type="button" disabled={busy} onClick={() => play({ kind: "set-badge", id: e.id, value: (e.badge ?? 0) + 1 }, `${e.name} +1`)}>+</button>}
                <button type="button" disabled={busy || locked} onClick={() => play({ kind: "end-effect", id: e.id }, `${e.name} ended`)}>×</button>
              </span>
            </li>
          );
        })}
      </ul>
      <p class="inline">
        <input type="search" placeholder={records ? "Add an Effect by name" : "Loading…"} value={text} disabled={!records} onInput={(e) => setText((e.target as HTMLInputElement).value)} style="flex:1 1 12rem" />
        <input type="number" min="0" inputMode="numeric" placeholder="level" value={level} onInput={(e) => setLevel((e.target as HTMLInputElement).value)} style="width:5rem" title="The Effect's level (a heightened spell's rank); blank keeps the Entry's" />
      </p>
      {results.length > 0 && (
        <ul class="results">
          {results.map((r) => (
            <li key={r.u}>
              <button type="button" class="linkish" disabled={busy} onClick={() => void add(r)}>{r.n}</button>
              <span><span class="tag">{r.p}</span> {!r.rm && <span class="tag legacy">Legacy</span>}</span>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function Resources({ session, maxima, busy, play }: { session: NonNullable<Character["session"]>; maxima: SessionMaxima; busy: boolean; play: Play }) {
  const resources = Object.entries(maxima.resources);
  const uses = Object.entries(maxima.uses);
  if (resources.length === 0 && uses.length === 0) return null;
  return (
    <details open>
      <summary>Resources and uses ({resources.length + uses.length})</summary>
      <ul class="plain items">
        {resources.map(([key, r]) => (
          <li key={key}>
            <span><strong>{r.label}</strong> {session.resources[key] ?? r.max} / {r.max}{r.renew === "daily" ? <span class="muted"> · daily</span> : null}</span>
            <span class="actions">
              <button type="button" disabled={busy || (session.resources[key] ?? r.max) <= 0} onClick={() => play({ kind: "spend-resource", key }, `${r.label} spent`)}>−</button>
              <button type="button" disabled={busy || (session.resources[key] ?? r.max) >= r.max} onClick={() => play({ kind: "restore-resource", key }, `${r.label} restored`)}>+</button>
            </span>
          </li>
        ))}
        {uses.map(([uuid, u]) => (
          <li key={uuid}>
            <span><strong>{u.name}</strong> {u.max - (session.uses[uuid] ?? 0)} / {u.max} <span class="muted">{describePer(u.per)}</span></span>
            <span class="actions">
              <button type="button" disabled={busy || (session.uses[uuid] ?? 0) >= u.max} onClick={() => play({ kind: "use-entry", uuid }, `${u.name} used`)}>Use</button>
              <button type="button" disabled={busy || (session.uses[uuid] ?? 0) <= 0} onClick={() => play({ kind: "restore-entry", uuid }, `${u.name} restored`)}>+</button>
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function Consumables({ session, busy, play }: { session: NonNullable<Character["session"]>; busy: boolean; play: Play }) {
  const [records, setRecords] = useState<IndexRecord[]>();
  const [text, setText] = useState("");
  const [quantity, setQuantity] = useState("1");
  useEffect(() => {
    loadCatalogue().then((all) => setRecords(all.filter((r) => r.t === "consumable" || r.t === "ammo")), () => setRecords([]));
  }, []);
  const results = records && text ? searchCatalogue(records, { text, legacy: true, limit: 8 }) : [];
  const add = async (record: IndexRecord) => {
    const entry = await loadEntry(record.u);
    if (!entry) return;
    const uses = entry.system.uses as { value?: unknown; max?: unknown; autoDestroy?: unknown } | undefined;
    const max = typeof uses?.max === "number" ? uses.max : 1;
    const q = Math.max(1, Math.trunc(Number(quantity)) || 1);
    const wand = entry.type === "consumable" && (entry.system.category === "wand" || uses?.autoDestroy === false);
    play({ kind: "add-consumable-stack", stack: { uuid: entry.uuid, name: entry.name, quantity: q, uses: max > 1 || wand ? { value: max, max, renew: wand ? "day" : null } : null, autoDestroy: uses?.autoDestroy !== false, ammunition: entry.type === "ammo" } }, `${entry.name} added`);
    setText("");
  };
  return (
    <details open={session.consumables.length > 0}>
      <summary>Consumables and ammunition ({session.consumables.length})</summary>
      <ul class="plain items">
        {session.consumables.map((s) => (
          <li key={s.id}>
            <span><strong>{s.name}</strong> ×{s.quantity}{s.uses ? <span class="muted"> · {s.uses.value} / {s.uses.max} uses{s.uses.renew ? ", daily" : ""}</span> : null}{s.ammunition ? <span class="tag">ammunition</span> : null}</span>
            <span class="actions">
              {s.ammunition
                ? <button type="button" disabled={busy || s.quantity <= 0} onClick={() => play({ kind: "fire-ammunition", id: s.id }, `${s.name} fired`)}>Fire</button>
                : <button type="button" disabled={busy || s.quantity <= 0 || (s.uses !== null && s.uses.value <= 0)} onClick={() => play({ kind: "use-consumable", id: s.id }, `${s.name} used`)}>Use</button>}
            </span>
          </li>
        ))}
      </ul>
      <p class="inline">
        <input type="search" placeholder={records ? "Add a consumable or ammunition by name" : "Loading…"} value={text} disabled={!records} onInput={(e) => setText((e.target as HTMLInputElement).value)} style="flex:1 1 12rem" />
        <input type="number" min="1" inputMode="numeric" value={quantity} onInput={(e) => setQuantity((e.target as HTMLInputElement).value)} style="width:5rem" title="Quantity" />
      </p>
      {results.length > 0 && (
        <ul class="results">
          {results.map((r) => (
            <li key={r.u}>
              <button type="button" class="linkish" disabled={busy} onClick={() => void add(r)}>{r.n}</button>
              <span><span class="tag">{r.t}</span>{r.l !== null ? <span class="muted"> · {r.l}</span> : null} {!r.rm && <span class="tag legacy">Legacy</span>}</span>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function Spellcasting({ session, maxima, sheet, busy, play }: { session: NonNullable<Character["session"]>; maxima: SessionMaxima; sheet: DerivedSheet; busy: boolean; play: Play }) {
  const names = new Map(sheet.spellcasting.flatMap((e) => e.spells.map((s) => [s.uuid, s.name] as const)));
  return (
    <details open>
      <summary>Spellcasting</summary>
      {maxima.spellcasting.map((entry) => {
        const use = session.spellcasting[entry.id] ?? { slots: {}, prepared: {}, innate: {} };
        const view = sheet.spellcasting.find((e) => e.id === entry.id);
        return (
          <div key={entry.id}>
            <strong>{entry.name}</strong> <span class="muted">{entry.casting}{view?.statistic ? ` · DC ${view.statistic.dc}` : ""}</span>
            <ul class="plain items">
              {Object.entries(entry.slots).map(([rank, slot]) => {
                const spent = entry.casting === "prepared" ? (use.prepared[rank] ?? []).filter(Boolean).length : (use.slots[rank] ?? 0);
                return (
                  <li key={rank}>
                    <span>
                      Rank {rank}: {slot.max - spent} / {slot.max}
                      {slot.prepared && (
                        <span class="muted">
                          {" "}· {slot.prepared.map((uuid, i) => (uuid ? `${names.get(uuid) ?? "?"}${use.prepared[rank]?.[i] ? " (cast)" : ""}` : "empty")).join(", ")}
                        </span>
                      )}
                    </span>
                    <span class="actions">
                      <button type="button" disabled={busy || spent >= slot.max} onClick={() => play({ kind: "spend-slot", entry: entry.id, rank }, `${entry.name} rank ${rank} slot`)}>Cast</button>
                      <button type="button" disabled={busy || spent <= 0} onClick={() => play({ kind: "restore-slot", entry: entry.id, rank }, `${entry.name} rank ${rank} restored`)}>+</button>
                    </span>
                  </li>
                );
              })}
              {entry.charges && (
                <li>
                  <span>Charges: {entry.charges.max - (use.charges ?? 0)} / {entry.charges.max}</span>
                  <span class="actions">
                    <button type="button" disabled={busy || (use.charges ?? 0) <= 0} onClick={() => play({ kind: "restore-slot", entry: entry.id, rank: "1" }, `${entry.name} charge restored`)}>+1</button>
                  </span>
                </li>
              )}
              {entry.charges?.spells.map((spell) => (
                <li key={spell.uuid}>
                  <span>{spell.name} <span class="muted">· {spell.rank === 0 ? "cantrip, free" : `${spell.rank} charge${spell.rank === 1 ? "" : "s"}`}</span></span>
                  <span class="actions">
                    <button type="button" disabled={busy || (spell.rank > 0 && (use.charges ?? 0) + spell.rank > entry.charges!.max)} onClick={() => play({ kind: "spend-slot", entry: entry.id, rank: String(spell.rank) }, `${spell.name} cast from ${entry.name}`)}>Cast</button>
                  </span>
                </li>
              ))}
              {Object.entries(entry.innate).map(([spell, innate]) => (
                <li key={spell}>
                  <span>{innate.name}: {innate.max - (use.innate[spell] ?? 0)} / {innate.max}</span>
                  <span class="actions">
                    <button type="button" disabled={busy || (use.innate[spell] ?? 0) >= innate.max} onClick={() => play({ kind: "spend-innate", entry: entry.id, spell }, `${innate.name} cast`)}>Cast</button>
                    <button type="button" disabled={busy || (use.innate[spell] ?? 0) <= 0} onClick={() => play({ kind: "restore-innate", entry: entry.id, spell }, `${innate.name} restored`)}>+</button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </details>
  );
}

/** Two-tap confirmation: the first tap arms, the second within a few seconds acts. */
export function ConfirmButton({ label, confirm, disabled, danger, onConfirm }: { label: string; confirm: string; disabled?: boolean; danger?: boolean; onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return armed ? (
    <button type="button" class={danger ? "danger" : "primary"} disabled={disabled} onClick={() => { setArmed(false); onConfirm(); }}>{confirm}</button>
  ) : (
    <button type="button" disabled={disabled} onClick={() => setArmed(true)}>{label}</button>
  );
}
